import React, { useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';
import { connect } from 'react-redux';
import Accordion from './Accordion';
import { setScrolledActiveSection, setHashValueWithOffset } from '../AmexUtils';
import { sanitizeString } from '../../../../../../ui-kitchen/src/utils/sanitizeString';
import './AmexFAQs.scss';

const AmexFAQs = (
  {
    awsFolderName,
    baseRequestUrl,
    faqsConfig: {
      title,
      section_id: sectionId,
      section_icon: sectionIcon = '',
      subsections,
      extra_copy: extraCopy,
      icon_plus_minus: iconPlusMinus,
      icon_position: iconPosition,
    } = {},
    dispatch,
    isSectionScroll,
    matchMedia,
    hash,
    eventAnalytics,
    printContent,
    isResyCredit2026Enabled,
  }
) => {
  if (!title || !subsections) return null;
  const requiredKeys = ['question', 'answer'];
  const filteredSubsections = subsections
    .filter((item) => requiredKeys.every((i) => Object.prototype.hasOwnProperty.call(item, i) && item[i]));

  if (filteredSubsections.length === 0) return null;

  const urlPrefix = `${baseRequestUrl}${awsFolderName}/images/`;
  const icon = `${urlPrefix}${sectionIcon}`;

  const sanitizedTitle = sanitizeString(title);
  const sanitizedExtraCopy = sanitizeString(extraCopy);

  const renderTitle = () => (printContent
    ? printContent(sanitizedTitle, 'sanitizedTitle') : sanitizedTitle);

  const renderContainer = () => (
    <div className="container">
      <div
        className={cx('AmexFAQs__wrapper',
          { titleNonRefresh: !isResyCredit2026Enabled && !matchMedia?.md })}
      >
        {sectionIcon && (
        <img
          className="AmexFAQs__icon"
          src={icon}
          alt=""
        />
        )}
        <h2
          id={sectionId}
          className={cx('AmexFAQs__title',
            { titleNonRefresh: !isResyCredit2026Enabled && !matchMedia?.md })}
          dangerouslySetInnerHTML={{ // eslint-disable-line react/no-danger
            __html: renderTitle(),
          }}
        />
        <ul className="AmexFAQs__content">
          {filteredSubsections.map((item, index) => (
            <Accordion
              key={item.question}
              question={item.question}
              answer={item.answer}
              id={index}
              hash={hash}
              eventAnalytics={eventAnalytics}
              iconPosition={iconPosition}
              iconPlusMinus={iconPlusMinus}
              printContent={printContent}
            />
          ))}
        </ul>
        {extraCopy && (
        <div
          className="AmexFAQs__extra"
          dangerouslySetInnerHTML={{ // eslint-disable-line react/no-danger
            __html: sanitizedExtraCopy,
          }}
        />
        )}
      </div>
    </div>
  );

  const renderWithSectionScroll = () => (
    <section
      data-section
      id={`${sectionId}_section`}
      className="AmexFAQs"
    >
      {renderContainer()}
    </section>
  );

  const renderWithOutSectionScroll = () => (
    <section
      className="AmexFAQs"
    >
      {renderContainer()}
    </section>
  );

  if (isSectionScroll) {
    const isMobile = !matchMedia?.md;
    setHashValueWithOffset(isMobile, 210, 200, sectionId, hash);
    const sections = useRef([]);

    useEffect(() => {
      sections.current = document.querySelectorAll('[data-section]');
      window.addEventListener('scroll', () => { setScrolledActiveSection(sections, dispatch); });

      return () => {
        window.removeEventListener('scroll', () => { setScrolledActiveSection(sections, dispatch); });
      };
    }, []);

    return renderWithSectionScroll();
  }
  return renderWithOutSectionScroll();
};

AmexFAQs.propTypes = {
  awsFolderName: PropTypes.string,
  baseRequestUrl: PropTypes.string,
  faqsConfig: PropTypes.shape({
    event_tag: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired,
    section_id: PropTypes.string.isRequired,
    section_icon: PropTypes.string,
    subsections: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
    extra_copy: PropTypes.string,
    icon_plus_minus: PropTypes.bool,
    icon_position: PropTypes.string,
  }).isRequired,
  dispatch: PropTypes.func.isRequired,
  isSectionScroll: PropTypes.bool,
  matchMedia: PropTypes.shape({
    xs: PropTypes.bool,
    sm: PropTypes.bool,
    md: PropTypes.bool,
    lg: PropTypes.bool,
    xl: PropTypes.bool,
  }).isRequired,
  hash: PropTypes.string.isRequired,
  eventAnalytics: PropTypes.func,
  printContent: PropTypes.func,
  isResyCredit2026Enabled: PropTypes.bool,
};

AmexFAQs.defaultProps = {
  awsFolderName: '',
  baseRequestUrl: '',
  isSectionScroll: false,
  eventAnalytics: null,
  printContent: null,
  isResyCredit2026Enabled: false,
};

export { AmexFAQs as ComponentUnderTest };
export default connect()(AmexFAQs);
